import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, PoundSterling, Search, X, Plus, Trash2, RefreshCw, ChevronDown, ChevronUp, Printer, Users, FilePlus, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import PrintPriceListModal from './PrintPriceListModal';
import CustomerGroupModal from './CustomerGroupModal';
import { groupBySupplier } from '../utils/groupBySupplier';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORANGE = '#ee7203';
const UNIT_OPTIONS = ['Each', 'M2', 'M', 'Length'];

const SOURCE_STYLE = {
  order:  { bg: '#dcfce7', text: '#166534' },
  quote:  { bg: '#eff6ff', text: '#1d4ed8' },
  manual: { bg: '#fff7ed', text: '#c2410c' },
};

// The three views of the price list. Quoted is the default.
const PRICE_TABS = [
  { id: 'quoted', label: 'Quoted' },
  { id: 'bought', label: 'Last Bought' },
  { id: 'all', label: 'All' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch (_) { return iso; }
};
const unitLabel = (u) => (u === 'M2' ? 'm²' : (u || 'Each').toLowerCase() === 'each' ? 'each' : u);
const money = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayIso = () => new Date().toISOString().split('T')[0];

// Same ranking the backend uses to decide which price is "newest": the later date wins, and on the same date a manually added
// price beats an order which beats a quote.
const SOURCE_RANK = { manual: 3, order: 2, quote: 1 };
const isNewer = (a, b) => {
  const da = a.price_date || '';
  const dbb = b.price_date || '';
  if (da !== dbb) return da > dbb;
  return (SOURCE_RANK[a.source] || 0) > (SOURCE_RANK[b.source] || 0);
};
const isQuotedPrice = (p) => p.source === 'quote' || p.source === 'manual';
const isBoughtPrice = (p) => p.source === 'order';

// The newest price per product code among the prices that pass `pred`.
const bestByCode = (docs, pred) => {
  const best = new Map();
  docs.forEach((p) => {
    if (!p.code || !pred(p)) return;
    const cur = best.get(p.code);
    if (!cur || isNewer(p, cur)) best.set(p.code, p);
  });
  return best;
};

// Puts a code -> price map back into the supplier / prefix groups the price list is shown in (same order as the backend).
const groupPrices = (bestMap, metaByKey) => {
  const byGroup = new Map();
  bestMap.forEach((p) => {
    const key = `${p.supplier}|${p.prefix}`;
    if (!byGroup.has(key)) {
      const meta = metaByKey.get(key) || {};
      byGroup.set(key, {
        supplier: p.supplier, prefix: p.prefix,
        sub_category: meta.sub_category || '', note: meta.note || '', has_manual: !!meta.has_manual, prices: [],
      });
    }
    byGroup.get(key).prices.push(p);
  });
  const ordered = [...byGroup.values()];
  ordered.sort((a, b) =>
    ((a.supplier === 'Other') - (b.supplier === 'Other'))
    || String(a.supplier).toLowerCase().localeCompare(String(b.supplier).toLowerCase())
    || String(a.prefix).localeCompare(String(b.prefix)));
  ordered.forEach((g) => g.prices.sort((a, b) => String(a.code || '').localeCompare(String(b.code || ''))));
  return ordered;
};

// ── Add price modal ─────────────────────────────────────────────────────────────────────────────────────────────────
// Uses the same product search the quote line items use (ProductSearchDropdown from Quotes.jsx, which calls the Stock
// Database search), so a price is always added against a real product code.
function AddPriceModal({ isOpen, onClose, account, ProductSearch, onAdded }) {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const [search, setSearch] = useState('');
  const [product, setProduct] = useState(null);
  const [price, setPrice] = useState('');
  const [unit, setUnit] = useState('Each');
  const [date, setDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) { setSearch(''); setProduct(null); setPrice(''); setUnit('Each'); setDate(todayIso()); }
  }, [isOpen]);

  if (!isOpen) return null;

  const save = async () => {
    if (!product?.code) return toast.error('Pick a product first');
    const value = parseFloat(price);
    if (!(value > 0)) return toast.error('Enter a price greater than zero');
    setSaving(true);
    try {
      const res = await api.post(`${API}/customer-pricing/price`, {
        account_id: account?.id || null,
        customer_name: account?.name || '',
        code: product.code,
        description: product.description || '',
        unit_price: value,
        unit,
        price_date: date,
      });
      toast.success('Price added');
      onAdded?.(res.data?.price);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add price');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed left-0 right-0 bottom-0 flex items-center justify-center p-4 bg-black/50"
      style={{ top: modalUiV2 ? activityBar.headerHeight : 0, zIndex: 120 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="customer-add-price-overlay"
    >
      <div className="w-full max-w-[560px] rounded-3xl bg-white shadow-2xl border-2 border-orange-300" data-testid="customer-add-price-modal">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100">
          <div className="min-w-0">
            <h3 className="text-base font-extrabold text-gray-900 leading-tight">Add Price</h3>
            <p className="text-[11px] text-gray-500 truncate">{account?.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Product (code or description)</Label>
            <div className="mt-1">
              {ProductSearch ? (
                <ProductSearch
                  value={search}
                  onChange={(v) => { setSearch(v); if (product) setProduct(null); }}
                  onSelect={(p) => {
                    const code = p.product_code || p.code || '';
                    setProduct({ code, description: p.description || p.name || '' });
                    setSearch(code);
                  }}
                  placeholder="Search products…"
                />
              ) : (
                <div className="text-xs text-gray-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading product search…</div>
              )}
            </div>
            {product && (
              <div className="mt-2 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-xs">
                <span className="font-mono font-bold text-gray-900">{product.code}</span>
                <span className="text-gray-600"> — {product.description}</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Price (£)</Label>
              <Input
                type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)}
                className="mt-1 rounded-lg border-2 border-gray-300 h-10" data-testid="customer-add-price-value"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Per</Label>
              <select
                value={unit} onChange={(e) => setUnit(e.target.value)}
                className="mt-1 w-full border-2 border-gray-300 rounded-lg px-2 h-10 text-sm bg-white focus:border-orange-400 focus:outline-none"
                data-testid="customer-add-price-unit"
              >
                {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Date</Label>
              <Input
                type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="mt-1 rounded-lg border-2 border-gray-300 h-10" data-testid="customer-add-price-date"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="rounded-xl h-10 px-4 text-sm">Cancel</Button>
            <Button
              onClick={save} disabled={saving}
              className="rounded-xl h-10 px-5 text-sm text-white font-semibold" style={{ backgroundColor: ORANGE }}
              data-testid="customer-add-price-save"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Saving…</> : 'Add Price'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// One price on the list. Optional buttons on the right:
//   onAdd      orange "+" — put this product on the quote that is open (only passed when the panel is inside the Create Quote window)
//   onRestore  "Use" — make this older price the current price again (only passed for archived prices)
//   onDelete   bin — remove a manually added price
function PriceRow({ price, onDelete, onAdd, onRestore, selectable = false, selected = false, onToggle }) {
  const st = SOURCE_STYLE[price.source] || SOURCE_STYLE.quote;
  // Inside the Create Quote window (onAdd is passed) the row is compact: the full description gets the room, and only the price
  // and its date are shown — no "Last priced / Manually added" tag and no quote reference.
  const compact = !!onAdd;
  const rowGrid = compact
    ? 'grid-cols-[100px_minmax(0,1fr)_110px_72px_56px]'
    : selectable
      ? 'grid-cols-[20px_96px_minmax(0,1fr)_104px_168px_64px]'
      : 'grid-cols-[96px_minmax(0,1fr)_104px_168px_64px]';
  return (
    <div
      className={`grid ${rowGrid} gap-3 items-center px-3 py-2 border-t border-gray-100 ${selected ? 'bg-orange-100/70' : 'hover:bg-orange-50/40'} ${selectable ? 'cursor-pointer' : ''}`}
      onClick={selectable ? () => onToggle?.(price) : undefined}
      data-testid={`customer-price-${price.code}`}
    >
      {selectable && (
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle?.(price)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 cursor-pointer accent-orange-500"
          data-testid={`customer-price-select-${price.code}`}
        />
      )}
      <div className="font-mono font-bold text-xs text-gray-900 truncate">{price.code}</div>
      {compact ? (
        <div
          className="text-xs text-gray-700 break-words"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          title={price.description}
        >
          {price.description || '—'}
        </div>
      ) : (
        <div
          className="text-xs text-gray-600 break-words"
          style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          title={price.description}
        >
          {price.description || '—'}
        </div>
      )}
      <div className="text-right">
        <span className="text-sm font-bold text-gray-900 tabular-nums">{money(price.unit_price)}</span>
        <span className="text-[10px] text-gray-400"> / {unitLabel(price.unit)}</span>
      </div>
      {compact ? (
        <div className="text-[11px] text-gray-500 whitespace-nowrap">{fmtDate(price.price_date)}</div>
      ) : (
        <div className="flex flex-row-reverse items-center justify-start gap-2 min-w-0">
          <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap" style={{ background: st.bg, color: st.text }}>
            {price.label}
          </span>
          <span className="text-[11px] text-gray-500 truncate">
            {fmtDate(price.price_date)}
          </span>
        </div>
      )}
      <div className="flex items-center justify-end gap-1">
        {onAdd && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAdd(price); }}
            className="w-6 h-6 rounded-full flex items-center justify-center text-white hover:opacity-90 flex-shrink-0"
            style={{ backgroundColor: ORANGE }}
            title={price.line_meta?.margin_config ? 'Add to this quote with its saved pricing dropdown' : 'Add to this quote'}
            data-testid={`customer-price-add-${price.code}`}
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
        {onRestore && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRestore(price); }}
            className="flex items-center gap-1 rounded-full border border-orange-300 text-orange-700 text-[10px] font-bold px-2 py-0.5 hover:bg-orange-50 whitespace-nowrap flex-shrink-0"
            title="Use this older price as the current price again"
            data-testid={`customer-price-restore-${price.code}`}
          >
            <RotateCcw className="w-3 h-3" /> Use
          </button>
        )}
        {price.source === 'manual' && onDelete && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(price); }} className="text-gray-300 hover:text-red-500 flex-shrink-0" title="Delete this manually added price" data-testid={`customer-price-delete-${price.code}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Pricing tab of the Customer Management side panel — the customer's live price list.
 * Built automatically from their quotes and orders plus prices added by hand, grouped by supplier and prefix. Three views:
 *   Quoted       (default) the newest price the customer was QUOTED (or had added by hand) for each product
 *   Last Bought  the newest price the customer actually BOUGHT each product at (from their orders)
 *   All          the newest price per product whichever it came from, with the older prices in the Archived section at the
 *                bottom — any of those can be put back as the current price with "Use"
 * The editable sub-category name and note are shown on groups that contain a manually added price.
 *
 *   onCreateQuote  (optional) shows the Create Quote button (tick products, open them on a new quote)
 *   onAddToQuote   (optional) shows a "+" on every product that adds it straight onto the quote that is open — used when this
 *                  panel slides out beside the Create Quote window. Prices that came from a quote bring their pricing dropdown
 *                  (List Price, Discount, Rebate ...) with them.
 *   refreshKey     change this to reload the list (the parent bumps it every time a quote window closes)
 */
export default function CustomerPricingPanel({ account, onCreateQuote, onAddToQuote, refreshKey = 0 }) {
  const [data, setData] = useState({ groups: [], archive: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('quoted');
  const [showArchive, setShowArchive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [customerGroup, setCustomerGroup] = useState(null);
  const [ProductSearch, setProductSearch] = useState(null);
  // "Create Quote" selection mode: tick products on the price list, then open the normal Create Quote modal with them on it.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    let alive = true;
    import('../pages/Quotes').then((m) => { if (alive) setProductSearch(() => m.default.ProductSearchDropdown); });
    return () => { alive = false; };
  }, []);

  const load = async (force = false) => {
    if (!account?.id && !account?.name) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`${API}/customer-pricing/list`, { params: { account_id: account.id || '', name: account.name || '', force } });
      const groups = res.data?.groups || [];
      const archive = res.data?.archive || [];
      setData({ groups, archive });
      // A price can disappear after a reload — never keep a ticked price that is no longer on the list.
      const knownIds = new Set([...groups.flatMap((g) => g.prices.map((p) => p.id)), ...archive.map((p) => p.id)]);
      setSelectedIds((prev) => new Set([...prev].filter((id) => knownIds.has(id))));
      setCustomerGroup(res.data?.customer_group || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load the price list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setSelectMode(false); setSelectedIds(new Set()); load(false); }, [account?.id, account?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // A quote window closed (Save / PDF / Email may have just added prices) — reload straight away.
  useEffect(() => { if (refreshKey) load(true); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMeta = async (group, patch) => {
    const next = { ...group, ...patch };
    try {
      await api.put(`${API}/customer-pricing/group-meta`, {
        account_id: account?.id || null,
        customer_name: account?.name || '',
        supplier: group.supplier,
        prefix: group.prefix,
        sub_category: next.sub_category || '',
        note: next.note || '',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  const setLocalMeta = (group, patch) =>
    setData((prev) => ({
      ...prev,
      groups: prev.groups.map((g) => (g.supplier === group.supplier && g.prefix === group.prefix ? { ...g, ...patch } : g)),
    }));

  const deletePrice = async (price) => {
    try {
      await api.delete(`${API}/customer-pricing/price/${price.id}`);
      toast.success('Price deleted');
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete price');
    }
  };

  // Puts an older price back as the current one: it is added again as a manually added price dated today, which is newer than
  // everything else, so it becomes the active price (and the quote's pricing dropdown it came with comes back too).
  const restorePrice = async (price) => {
    try {
      await api.post(`${API}/customer-pricing/price`, {
        account_id: account?.id || null,
        customer_name: account?.name || '',
        code: price.code,
        description: price.description || '',
        unit_price: parseFloat(price.unit_price),
        unit: price.unit || 'Each',
        price_date: todayIso(),
        line_meta: price.line_meta || null,
      });
      toast.success(`${price.code} is back at ${money(price.unit_price)}`);
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore price');
    }
  };

  const changeTab = (next) => { setTab(next); setSelectedIds(new Set()); };

  const q = query.trim().toLowerCase();
  const matches = (p, g) => {
    if (!q) return true;
    return `${p.code} ${p.description} ${g ? `${g.supplier} ${g.prefix} ${g.sub_category} ${g.note}` : ''}`.toLowerCase().includes(q);
  };

  const allDocs = useMemo(() => [...data.groups.flatMap((g) => g.prices), ...data.archive], [data.groups, data.archive]);
  const metaByKey = useMemo(() => {
    const m = new Map();
    data.groups.forEach((g) => m.set(`${g.supplier}|${g.prefix}`, g));
    return m;
  }, [data.groups]);

  const quotedBest = useMemo(() => bestByCode(allDocs, isQuotedPrice), [allDocs]);
  const boughtBest = useMemo(() => bestByCode(allDocs, isBoughtPrice), [allDocs]);
  const totalActive = data.groups.reduce((n, g) => n + g.prices.length, 0);
  const tabCounts = { quoted: quotedBest.size, bought: boughtBest.size, all: totalActive };

  // The groups shown for the chosen tab.
  const tabGroups = useMemo(() => {
    if (tab === 'all') return data.groups;
    return groupPrices(tab === 'bought' ? boughtBest : quotedBest, metaByKey);
  }, [tab, data.groups, quotedBest, boughtBest, metaByKey]);

  const visibleGroups = useMemo(
    () => tabGroups
      .map((g) => ({ ...g, prices: g.prices.filter((p) => matches(p, g)) }))
      .filter((g) => g.prices.length > 0),
    [tabGroups, q], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const visibleArchive = useMemo(
    () => data.archive.filter((p) => matches(p, null)),
    [data.archive, q], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const toggleSelected = (price) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(price.id)) next.delete(price.id); else next.add(price.id);
      return next;
    });

  const toggleGroupSelected = (group) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allTicked = group.prices.every((p) => next.has(p.id));
      group.prices.forEach((p) => { if (allTicked) next.delete(p.id); else next.add(p.id); });
      return next;
    });

  const selectAllShown = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleGroups.forEach((g) => g.prices.forEach((p) => next.add(p.id)));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const createQuoteFromSelection = () => {
    // In list order (supplier, prefix, code) so the quote lines follow the price list; only the prices of the tab being viewed.
    const chosen = tabGroups.flatMap((g) => g.prices).filter((p) => selectedIds.has(p.id));
    if (chosen.length === 0) { toast.error('Tick at least one product first'); return; }
    onCreateQuote?.(chosen);
    exitSelectMode();
  };

  const emptyText = {
    quoted: 'No quoted prices yet — a price appears here once a quote for this customer has been saved, downloaded or emailed, or when you use Add Price.',
    bought: 'Nothing bought yet — prices appear here from this customer\'s orders.',
    all: 'No prices on file yet — they are added automatically from this customer\'s quotes and orders, or use Add Price.',
  }[tab];

  return (
    <div className="h-full flex flex-col" data-testid="customer-pricing-panel">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-orange-100 flex-shrink-0">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
        >
          <PoundSterling className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-extrabold text-gray-900 leading-tight">Pricing</h3>
          <p className="text-[11px] text-gray-500 truncate">
            {account?.name} — {totalActive} product price{totalActive === 1 ? '' : 's'} on file
            {customerGroup && (
              <span className="ml-1.5 text-orange-600 font-semibold" title={`Combined with: ${(customerGroup.members || []).map((m) => m.name).join(', ')}`}>
                · Grouped with {(customerGroup.members || []).length - 1} other account{(customerGroup.members || []).length - 1 === 1 ? '' : 's'}
              </span>
            )}
          </p>
        </div>
        <button onClick={() => load(true)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 flex-shrink-0" data-testid="customer-pricing-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <Button
          onClick={() => setAddOpen(true)}
          className="rounded-xl h-9 px-3 text-xs text-white font-semibold flex-shrink-0"
          style={{ backgroundColor: ORANGE }}
          data-testid="customer-pricing-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Price
        </Button>
        {onCreateQuote && (
          <Button
            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            disabled={totalActive === 0}
            variant="outline"
            className="rounded-xl h-9 px-3 text-xs font-semibold flex-shrink-0 border-2"
            style={selectMode ? { borderColor: ORANGE, color: '#ffffff', backgroundColor: ORANGE } : { borderColor: ORANGE, color: ORANGE }}
            title="Tick products on this price list and create a quote from them"
            data-testid="customer-pricing-create-quote-btn"
          >
            <FilePlus className="w-3.5 h-3.5 mr-1" /> Create Quote
          </Button>
        )}
        <Button
          onClick={() => setGroupOpen(true)}
          variant="outline"
          className="rounded-xl h-9 px-3 text-xs font-semibold flex-shrink-0 border-2"
          style={{ borderColor: ORANGE, color: ORANGE }}
          data-testid="customer-pricing-group-btn"
        >
          <Users className="w-3.5 h-3.5 mr-1" /> Customer Group
        </Button>
        <Button
          onClick={() => setPrintOpen(true)}
          disabled={totalActive === 0}
          variant="outline"
          className="rounded-xl h-9 px-3 text-xs font-semibold flex-shrink-0 border-2"
          style={{ borderColor: ORANGE, color: ORANGE }}
          data-testid="customer-pricing-print-btn"
        >
          <Printer className="w-3.5 h-3.5 mr-1" /> Print
        </Button>
      </div>

      {/* Quoted / Last Bought / All */}
      <div className="flex items-center gap-2 px-5 pt-3 flex-shrink-0" data-testid="customer-pricing-tabs">
        {PRICE_TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => changeTab(t.id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold border-2 transition-colors ${
                active ? 'text-white border-orange-500' : 'text-orange-700 bg-orange-50 border-orange-200 hover:bg-orange-100'
              }`}
              style={active ? { backgroundColor: ORANGE } : undefined}
              data-testid={`customer-pricing-tab-${t.id}`}
            >
              {t.label}
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-semibold ${active ? 'bg-white/25 text-white' : 'bg-orange-100 text-orange-700'}`}>
                {tabCounts[t.id]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search product code, description, supplier, prefix, sub-category or note…"
            data-testid="customer-pricing-search"
            className="w-full pl-8 pr-8 py-2 rounded-lg border border-gray-200 text-xs text-gray-700 placeholder-gray-300 focus:outline-none focus:border-orange-300 focus:ring-1 focus:ring-orange-200 bg-gray-50"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Building the price list…</span>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-sm text-red-600">{error}</div>
        ) : visibleGroups.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <PoundSterling className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm">{query ? `No prices match "${query}"` : emptyText}</p>
          </div>
        ) : (
          groupBySupplier(visibleGroups).map((s) => (
            <section key={s.key} className="rounded-xl border border-gray-200 overflow-hidden bg-white" data-testid={`customer-price-supplier-${s.testKey}`}>
              <div className="px-3 py-2 flex items-center gap-2 flex-wrap" style={{ backgroundColor: ORANGE }}>
                {selectMode && (
                  <input
                    type="checkbox"
                    checked={s.prices.length > 0 && s.prices.every((p) => selectedIds.has(p.id))}
                    onChange={() => toggleGroupSelected(s)}
                    className="w-4 h-4 cursor-pointer accent-orange-500"
                    title="Tick every product from this supplier"
                    data-testid={`customer-price-supplier-select-${s.testKey}`}
                  />
                )}
                <span className="text-xs font-bold text-white uppercase tracking-wide">{s.supplier}</span>
                {/* The prefix code(s) of this supplier's products sit up here on the header, in white (one supplier can have several: Unilin has 25Y and 28H) */}
                {[...new Set(s.groups.map((g) => g.prefix).filter(Boolean))].map((pfx) => (
                  <span
                    key={pfx}
                    className="text-[11px] font-mono font-bold text-white border border-white/80 rounded px-1.5 py-0.5 leading-none"
                    data-testid={`customer-price-supplier-prefix-${pfx}`}
                  >{pfx}</span>
                ))}
                <span className="text-[11px] text-white/80 ml-auto">{s.prices.length} price{s.prices.length === 1 ? '' : 's'}</span>
              </div>
              {s.groups.map((g) => (
              <div key={`${g.supplier}|${g.prefix}`} className="border-t border-orange-100" data-testid={`customer-price-group-${g.prefix}`}>
              <div className={`px-3 py-1.5 flex items-center gap-2 flex-wrap bg-white border-b border-orange-200 ${selectMode ? '' : 'hidden'}`}>
                {selectMode && (
                  <input
                    type="checkbox"
                    checked={g.prices.length > 0 && g.prices.every((p) => selectedIds.has(p.id))}
                    onChange={() => toggleGroupSelected(g)}
                    className="w-4 h-4 cursor-pointer accent-orange-500"
                    title="Tick every product in this prefix"
                    data-testid={`customer-price-group-select-${g.prefix}`}
                  />
                )}
                <span className="text-[11px] font-mono font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">{g.prefix}</span>
                <span className="text-[11px] text-gray-400 ml-auto">{g.prices.length} price{g.prices.length === 1 ? '' : 's'}</span>
              </div>
              {g.has_manual && (
                <div className="grid grid-cols-[minmax(0,220px)_minmax(0,1fr)] gap-2 px-3 py-2 bg-orange-50/60 border-b border-orange-100">
                  <input
                    value={g.sub_category}
                    onChange={(e) => setLocalMeta(g, { sub_category: e.target.value })}
                    onBlur={() => saveMeta(g, {})}
                    placeholder="Sub-category name"
                    className="rounded-lg border border-orange-200 bg-white px-2 py-1 text-xs font-semibold text-gray-800 focus:outline-none focus:border-orange-400"
                    data-testid={`customer-price-subcat-${g.prefix}`}
                  />
                  <input
                    value={g.note}
                    onChange={(e) => setLocalMeta(g, { note: e.target.value })}
                    onBlur={() => saveMeta(g, {})}
                    placeholder="Note for this group"
                    className="rounded-lg border border-orange-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:border-orange-400"
                    data-testid={`customer-price-note-${g.prefix}`}
                  />
                </div>
              )}
              <div>
                {g.prices.map((p) => (
                  <PriceRow
                    key={p.id}
                    price={p}
                    onDelete={deletePrice}
                    onAdd={onAddToQuote}
                    selectable={selectMode}
                    selected={selectedIds.has(p.id)}
                    onToggle={toggleSelected}
                  />
                ))}
              </div>
              </div>
              ))}
            </section>
          ))
        )}

        {/* Archive — older prices, hidden at the bottom of the All view. "Use" puts one back as the current price. */}
        {!loading && !error && tab === 'all' && data.archive.length > 0 && (
          <div className="rounded-xl border border-gray-200 bg-gray-50/70">
            <button
              onClick={() => setShowArchive((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left"
              data-testid="customer-pricing-archive-toggle"
            >
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Archived prices</span>
              <span className="text-[11px] bg-gray-200 text-gray-600 rounded-full px-1.5 py-0.5 font-semibold">{data.archive.length}</span>
              <span className="ml-auto">{showArchive ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}</span>
            </button>
            {showArchive && (
              <div className="bg-white rounded-b-xl">
                {visibleArchive.length === 0
                  ? <div className="px-3 py-3 text-xs text-gray-400 italic">No archived prices match.</div>
                  : visibleArchive.map((p) => <PriceRow key={p.id} price={p} onDelete={deletePrice} onRestore={restorePrice} />)}
              </div>
            )}
          </div>
        )}
      </div>

      {selectMode && (
        <div
          className="flex items-center gap-3 px-5 py-3 border-t-2 border-orange-300 bg-orange-50 flex-shrink-0 flex-wrap"
          data-testid="customer-pricing-select-bar"
        >
          <span className="text-xs font-bold text-orange-800" data-testid="customer-pricing-selected-count">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={selectAllShown}
            className="text-xs font-semibold text-orange-700 hover:underline"
            data-testid="customer-pricing-select-all"
          >
            Select all shown
          </button>
          <button
            type="button"
            onClick={clearSelection}
            disabled={selectedIds.size === 0}
            className="text-xs font-semibold text-gray-500 hover:underline disabled:opacity-40 disabled:no-underline"
            data-testid="customer-pricing-select-clear"
          >
            Clear
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" onClick={exitSelectMode} className="rounded-xl h-9 px-4 text-xs" data-testid="customer-pricing-select-cancel">
              Cancel
            </Button>
            <Button
              onClick={createQuoteFromSelection}
              disabled={selectedIds.size === 0}
              className="rounded-xl h-9 px-4 text-xs text-white font-semibold"
              style={{ backgroundColor: ORANGE }}
              data-testid="customer-pricing-select-create"
            >
              <FilePlus className="w-3.5 h-3.5 mr-1" /> Create Quote ({selectedIds.size})
            </Button>
          </div>
        </div>
      )}

      <PrintPriceListModal
        isOpen={printOpen}
        onClose={() => setPrintOpen(false)}
        account={account}
        groups={data.groups}
      />

      <CustomerGroupModal
        isOpen={groupOpen}
        onClose={() => setGroupOpen(false)}
        account={account}
        existingGroup={customerGroup}
        onGrouped={() => load(true)}
      />

      <AddPriceModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        account={account}
        ProductSearch={ProductSearch}
        onAdded={() => load(true)}
      />
    </div>
  );
}
